import { zodResolver } from "@hookform/resolvers/zod";
import type { TaskListColor } from "@orsane/schema/task-lists";
import { taskListColorSchema } from "@orsane/schema/task-lists";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Files } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { trpcClient } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { taskListRoute } from "@/routes/taskListRoute";
import EnterButton from "./buttons/EnterButton";
import ColorSelector from "./ColorSelector";
import { trpc } from "./QueryProvider";
import { Checkbox } from "./ui/checkbox";
import { Field, FieldError, FieldLabel } from "./ui/field";

// Form schema
const duplicateListFormSchema = z.object({
    name: z
        .string()
        .min(1, "List name is required")
        .min(2, "List name must be at least 2 characters")
        .max(100, "List name cannot exceed 100 characters"),
    color: taskListColorSchema,
    copyTasks: z.boolean(),
    copyAccess: z.boolean(),
});

type DuplicateListFormData = z.infer<typeof duplicateListFormSchema>;

interface DuplicateListDialogProps {
    taskList: {
        id: number;
        name: string;
        color?: TaskListColor;
    };
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export default function DuplicateListDialog({ taskList, open, onOpenChange }: DuplicateListDialogProps) {
    const [isLoading, setIsLoading] = useState(false);
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const form = useForm({
        resolver: zodResolver(duplicateListFormSchema),
        defaultValues: {
            name: `${taskList.name} (Copy)`,
            color: taskList.color || "Blue",
            copyTasks: true,
            copyAccess: true,
        },
    });

    useEffect(() => {
        form.reset({
            name: `${taskList.name} (Copy)`,
            color: taskList.color || "Blue",
            copyTasks: true,
            copyAccess: true,
        });
    }, []);

    const handleDuplicate = async (formData: DuplicateListFormData) => {
        try {
            setIsLoading(true);
            const data = await trpcClient.taskList.duplicate.mutate({
                id: taskList.id,
                name: formData.name,
                color: formData.color,
                copyTasks: formData.copyTasks,
                copyAccess: formData.copyAccess,
            });
            console.log(data);
            setTimeout(() => {
                navigate({
                    to: taskListRoute.to,
                    params: { taskListId: data.id },
                });
            });

            queryClient.invalidateQueries({ queryKey: trpc.taskList.list.queryOptions().queryKey });
            onOpenChange(false);
        } catch (error) {
            console.error("Failed to duplicate list:", error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="gap-6 rounded-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-lg font-medium">
                        <Files className="size-4" />
                        Duplicate task list '{taskList.name}'
                    </DialogTitle>
                    <DialogDescription className="text-orsane-muted">
                        Create a copy of the list '{taskList.name}' with customizable options.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={form.handleSubmit(handleDuplicate)} className="space-y-4">
                    <div
                        className={cn(
                            "flex items-start",
                            "file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex w-full min-w-0 rounded-md border bg-transparent pl-0 py-0 text-lg shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file: file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:",
                            "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
                            "has-[:focus-visible]:border-ring has-[:focus-visible]:ring-ring/50 has-[:focus-visible]:ring-[3px]",
                            "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive"
                        )}
                    >
                        <div>
                            <Controller
                                control={form.control}
                                name="color"
                                render={({ field }) => (
                                    <Field>
                                        <ColorSelector color={field.value || "Blue"} onApply={field.onChange} buttonClassName="size-9!" />
                                    </Field>
                                )}
                            />
                        </div>

                        <div className="flex-1">
                            <Controller
                                control={form.control}
                                name="name"
                                render={({ field, fieldState }) => (
                                    <Field className="gap-1">
                                        <Input
                                            data-1p-ignore
                                            {...field}
                                            placeholder="New list name"
                                            className="w-full appearance-none border-0 focus:outline-none focus-visible:ring-0"
                                        />
                                        {fieldState.invalid && (
                                            <div className="pl-2.5 pb-1">
                                                <FieldError errors={[fieldState.error]} />
                                            </div>
                                        )}
                                    </Field>
                                )}
                            />
                        </div>
                    </div>

                    <Controller
                        control={form.control}
                        name="copyTasks"
                        render={({ field }) => (
                            <Field orientation="horizontal">
                                <Checkbox id="form-copy-tasks" checked={field.value} onCheckedChange={field.onChange} />
                                <FieldLabel htmlFor="form-copy-tasks">Copy tasks</FieldLabel>
                            </Field>
                        )}
                    />

                    <Controller
                        control={form.control}
                        name="copyAccess"
                        render={({ field }) => (
                            <Field orientation="horizontal">
                                <Checkbox id="form-copy-access" checked={field.value} onCheckedChange={field.onChange} />
                                <FieldLabel htmlFor="form-copy-access">Copy user access</FieldLabel>
                            </Field>
                        )}
                    />

                    <DialogFooter>
                        <Button variant="outline" size="sm" type="button" onClick={() => onOpenChange(false)} disabled={isLoading}>
                            Cancel
                        </Button>
                        <EnterButton type="submit" disabled={isLoading} isSubmitting={isLoading}>
                            {isLoading ? "Duplicating..." : "Duplicate"}
                        </EnterButton>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
